import type { ReactNode } from 'react';

/**
 * Shared admin-dashboard primitives. The same HealthDot / Tag / ago started
 * life copy-pasted in AdminStatus.tsx and AdminStreaming.tsx; the Phase-2
 * observability surfaces (egress board, channel-health, alerts, concurrency
 * gauge) all want them, so they live here once. New components import from
 * this module; the original pages keep their local copies to limit churn.
 */

export function HealthDot({ status }: { status: string }) {
  const c =
    status === 'healthy' ? 'bg-emerald-500'
    : status === 'unhealthy' ? 'bg-rose-500'
    : 'bg-gray-300';
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${c}`} title={status} />;
}

export function Tag({ children, tone = 'gray' }: { children: ReactNode; tone?: 'gray' | 'amber' | 'emerald' | 'rose' }) {
  const c = {
    gray: 'bg-gray-100 text-gray-600',
    amber: 'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    rose: 'bg-rose-100 text-rose-700',
  }[tone];
  return <span className={`ml-1 inline-block rounded px-1.5 py-0.5 text-[10px] ${c}`}>{children}</span>;
}

export function ago(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'soon';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** A reliability score (0..1, or null when no samples) as a colored pill. */
export function ReliabilityPill({ score }: { score: number | null }) {
  if (score == null) return <span className="text-xs text-gray-400">no data</span>;
  const pct = Math.round(score * 100);
  const c =
    score >= 0.95 ? 'bg-emerald-100 text-emerald-700'
    : score >= 0.8 ? 'bg-amber-100 text-amber-700'
    : 'bg-rose-100 text-rose-700';
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${c}`}>{pct}%</span>;
}
